import {
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {useTranslation} from 'react-i18next'

import {CheckIcon, DownloadLinkIcon, RenderIf} from 'components'
import {DopamineAssistantFullscreenIcon} from './DopamineAssistantFullscreenIcon'
import {DopamineTableContext} from './DopamineAssistantTable.context'
import {DopamineAssistantTableDialog} from './DopamineAssistantTableDialog'
import {
  downloadDopamineTable,
  getDopamineTableData,
  getDopamineTableFilename,
} from './DopamineAssistantTable.utils'

const DOPAMINE_TABLE_ACTION_CLASSES =
  'dopamine-tip relative flex h-6 w-6 shrink-0 items-center justify-center rounded text-dopamine-muted hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-1 focus-visible:outline-dopamine-accent disabled:cursor-not-allowed disabled:opacity-40 [&_svg]:h-3.5 [&_svg]:w-3.5'

export const DopamineAssistantTable = ({
  children,
}: {
  children?: ReactNode
}): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const {question, isUpdating} = useContext(DopamineTableContext)
  const tableRef = useRef<HTMLTableElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [downloadStatus, setDownloadStatus] = useState<
    'idle' | 'done' | 'error'
  >('idle')
  const data = getDopamineTableData(children)

  useEffect(() => {
    if (downloadStatus === 'idle') return
    const timeout = window.setTimeout(() => setDownloadStatus('idle'), 2000)
    return () => window.clearTimeout(timeout)
  }, [downloadStatus])

  const download = (): void => {
    const table = tableRef.current
    const tables = table
      ?.closest('.dopamine-markdown')
      ?.querySelectorAll('table')
    const index = tables && table ? Array.from(tables).indexOf(table) : 0
    try {
      downloadDopamineTable(data, getDopamineTableFilename(question, index + 1))
      setDownloadStatus('done')
    } catch {
      setDownloadStatus('error')
    }
  }

  const downloadLabel = t(
    downloadStatus === 'done'
      ? 'DOPAMINE_TABLE_DOWNLOADED'
      : 'DOPAMINE_DOWNLOAD_TABLE',
  )
  const downloadButton = (
    <button
      aria-label={downloadLabel}
      className={DOPAMINE_TABLE_ACTION_CLASSES}
      data-tip={downloadLabel}
      disabled={isUpdating}
      onClick={download}
      type="button"
    >
      {downloadStatus === 'done' ? (
        <CheckIcon strokeColor="text-current" />
      ) : (
        <DownloadLinkIcon strokeColor="text-current" id="" />
      )}
    </button>
  )
  const downloadFeedback = (
    <>
      <span aria-live="polite" className="sr-only">
        {downloadStatus === 'done' ? t('DOPAMINE_TABLE_DOWNLOADED') : ''}
      </span>
      <RenderIf isTrue={downloadStatus === 'error'}>
        <p role="alert" className="px-3 text-xs">
          {t('DOPAMINE_TABLE_DOWNLOAD_ERROR')}
        </p>
      </RenderIf>
    </>
  )

  return (
    <div className="relative my-[0.8125rem]">
      <div className="dopamine-markdown-table !m-0">
        <table
          ref={tableRef}
          className="!w-max !min-w-full [&_td]:max-w-64 [&_tr>:last-child]:pr-[4.5rem]"
        >
          {children}
        </table>
      </div>
      <div className="dopamine-header-actions absolute right-0 top-1 z-2 flex gap-0.5 bg-gradient-to-l from-[#1a1c22] from-75% to-transparent pl-3 pr-1">
        {downloadButton}
        <button
          aria-label={t('DOPAMINE_EXPAND_TABLE')}
          aria-haspopup="dialog"
          className={DOPAMINE_TABLE_ACTION_CLASSES}
          data-tip={t('DOPAMINE_EXPAND_TABLE')}
          disabled={isUpdating}
          onClick={() => setIsExpanded(true)}
          type="button"
        >
          <DopamineAssistantFullscreenIcon isFullscreen={false} />
        </button>
      </div>
      {downloadFeedback}
      <RenderIf isTrue={isExpanded}>
        <DopamineAssistantTableDialog
          data={data}
          downloadButton={downloadButton}
          downloadFeedback={downloadFeedback}
          onClose={() => setIsExpanded(false)}
          title={
            data.headers
              .map(header => header.text)
              .filter(Boolean)
              .join(' · ') || t('DOPAMINE_TABLE')
          }
        />
      </RenderIf>
    </div>
  )
}
